import { applyH3PromoDiscount } from "./h3-promo";

export type ModelId =
  | "cs_480p_i2v_5s"
  | "cs_480p_i2v_8s"
  | "cs_720p_i2v_5s"
  | "cs_720p_i2v_8s"
  | "wan25_720p_i2v_5s"
  | "integrated_h3_i2v"
  | "qwen_image"
  | "qwen_image_edit"
  | "cs_t2v_480p_5s";

/** Clip length for the Enhanced (integrated) slider (seconds). */
export const INTEGRATED_DURATION_MIN = 5;
export const INTEGRATED_DURATION_MAX = 15;
export const INTEGRATED_MODEL_ID: ModelId = "integrated_h3_i2v";

/** Affiliate free-credit path — Classic Standard 5s only. */
export const AFFILIATE_DEFAULT_MODEL_ID: ModelId = "cs_480p_i2v_5s";

/** Product display name for the integrated (with-sound) tier. */
export const POWER_TIER_LABEL = "Enhanced";
export const CLASSIC_TIER_LABEL = "Classic";

export interface ModelOption {
  id: ModelId;
  label: string;
  tier: string;
  resolution: string;
  duration: string;
  /** Fixed charge, or CREDITS PER SECOND when `variableDuration` is set. */
  creditCost: number;
  description: string;
  mediaType: "VIDEO" | "IMAGE";
  requiresPurchase?: boolean;
  /** Native audio output. Drives the "Audio" tag + banner. */
  audio?: boolean;
  /** Small badge shown on the model tile, e.g. "NEW". */
  tag?: string;
  /** Client-selectable duration range; charge = creditCost * seconds. */
  variableDuration?: { min: number; max: number };
}

export const INTEGRATED_MODEL: ModelOption = {
  id: "integrated_h3_i2v",
  label: POWER_TIER_LABEL,
  tier: POWER_TIER_LABEL,
  resolution: "720p",
  duration: "5–15s",
  creditCost: 12,
  description: "720p video with native audio — pick 5–15 seconds",
  mediaType: "VIDEO",
  audio: true,
  requiresPurchase: true,
  variableDuration: {
    min: INTEGRATED_DURATION_MIN,
    max: INTEGRATED_DURATION_MAX,
  },
};

/** Kept for historical generations — not shown in picker. */
export const HD_AUDIO_MODEL_ID: ModelId = "wan25_720p_i2v_5s";
export const HD_AUDIO_MODEL: ModelOption = {
  id: "wan25_720p_i2v_5s",
  label: "HD Audio",
  tier: "HD Audio",
  resolution: "720p",
  duration: "5s",
  creditCost: 80,
  description: "Wan 2.5 — 720p video with native audio",
  mediaType: "VIDEO",
  requiresPurchase: true,
  audio: true,
};

/** Classic (silent) I2V tiles — CS has no duration toggle, so 5s/8s are separate tiles. */
export const CLASSIC_MODELS: ModelOption[] = [
  {
    id: "cs_480p_i2v_5s",
    label: "Standard",
    tier: "Standard",
    resolution: "480p",
    duration: "5s",
    creditCost: 42,
    description: "Fast generation, standard quality",
    mediaType: "VIDEO",
  },
  {
    id: "cs_480p_i2v_8s",
    label: "Standard 8s",
    tier: "Standard",
    resolution: "480p",
    duration: "8s",
    creditCost: 84,
    description: "Longer generation, standard quality",
    mediaType: "VIDEO",
    requiresPurchase: true,
  },
  {
    id: "cs_720p_i2v_5s",
    label: "Premium",
    tier: "Premium",
    resolution: "720p",
    duration: "5s",
    creditCost: 70,
    description: "Higher resolution, sharper details",
    mediaType: "VIDEO",
  },
  {
    id: "cs_720p_i2v_8s",
    label: "Premium 8s",
    tier: "Premium",
    resolution: "720p",
    duration: "8s",
    creditCost: 154,
    description: "Longer generation, higher resolution",
    mediaType: "VIDEO",
    requiresPurchase: true,
  },
];

export const I2V_MODELS: ModelOption[] = [...CLASSIC_MODELS, INTEGRATED_MODEL];

export function getClassicModels(): ModelOption[] {
  return CLASSIC_MODELS;
}

export function getModelOption(id: ModelId): ModelOption | undefined {
  if (id === INTEGRATED_MODEL_ID) return INTEGRATED_MODEL;
  if (id === HD_AUDIO_MODEL_ID) return HD_AUDIO_MODEL;
  return (
    CLASSIC_MODELS.find((m) => m.id === id) ||
    (id === T2V_MODEL.id ? T2V_MODEL : undefined)
  );
}

export function isClassicSilentModel(id: ModelId): boolean {
  return id.startsWith("cs_") && id.includes("i2v");
}

export const T2V_MODEL: ModelOption = {
  id: "cs_t2v_480p_5s",
  label: "Standard",
  tier: "Standard",
  resolution: "480p",
  duration: "5s",
  creditCost: 140,
  description: "Generate video from text description",
  mediaType: "VIDEO",
};

export const T2I_MODEL = {
  id: "qwen_image" as ModelId,
  label: "Basic",
  tier: "Basic",
  creditCost: 19,
  description: "Generate images from text descriptions",
  mediaType: "IMAGE" as const,
};

export const IMAGE_EDIT_MODEL = {
  id: "qwen_image_edit" as ModelId,
  label: "Basic",
  tier: "Basic",
  creditCost: 19,
  description: "Edit images with text instructions",
  mediaType: "IMAGE" as const,
};

/** Display / debit cost for a model at a given clip length. */
export function getGenerationCreditCost(model: ModelOption, durationSeconds?: number): number {
  const base = model.variableDuration
    ? model.creditCost * (durationSeconds ?? model.variableDuration.min)
    : model.creditCost;
  // Enhanced (H3) is auto-discounted during the flash promo. Reverts on its
  // own after the hardcoded deadline in `h3-promo.ts` — callers unchanged.
  return model.id === INTEGRATED_MODEL_ID ? applyH3PromoDiscount(base) : base;
}

export const CREDIT_COSTS: Record<ModelId, number> = {
  cs_480p_i2v_5s: 42,
  cs_480p_i2v_8s: 84,
  cs_720p_i2v_5s: 70,
  cs_720p_i2v_8s: 154,
  wan25_720p_i2v_5s: 80,
  integrated_h3_i2v: 12,
  qwen_image: 19,
  qwen_image_edit: 19,
  cs_t2v_480p_5s: 140,
};
